import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { mockReleases } from '../../data/mock-release-data';
import {
  getMockReleaseById,
  mockAssignedPlan,
  mockUsers,
} from '../../data/mock-testhub-data';
import { Release } from '../../models/release';
import { TeamMember } from '../../models/team-member';
import { TestPlan } from '../../models/test-plan';
import { UtilityService } from '../utility/utility.service';

@Injectable({
  providedIn: 'root',
})
export class ReleaseService {
  private utilityService = inject(UtilityService);

  releaseEndpoint = `${environment.testHubApiUrl}/v1/Release`;
  testPlanEndpoint = `${environment.testHubApiUrl}/v1/TestPlan`;
  userEndpoint = `${environment.testHubApiUrl}/v1/User`;

  getReleases(): Observable<Release[]> {
    if (environment.useMockData) return of(mockReleases);
    return this.utilityService.getRequest<Release[]>(this.releaseEndpoint);
  }

  getReleaseById(releaseId: number): Observable<Release> {
    if (environment.useMockData) return of(getMockReleaseById(releaseId));
    return this.utilityService.getRequest<Release>(`${this.releaseEndpoint}/${releaseId}`);
  }

  postRelease(releaseAzureId: number, releaseName: string): Observable<Release> {
    var body = {
      releaseAzureId,
      releaseName,
      modifiedByUserId: 1,
    };
    return this.utilityService.postRequest<Release>(this.releaseEndpoint, body);
  }

  /** Claim a plan for a tester (PBI 55095). */
  assignPlan(testPlanId: number, userId: number): Observable<TestPlan> {
    if (environment.useMockData) return of(mockAssignedPlan(testPlanId, userId, 'You'));
    return this.utilityService.postRequest<TestPlan>(
      `${this.testPlanEndpoint}/${testPlanId}/Assign/${userId}`,
      {},
    );
  }

  /** Return a plan to the unassigned pool. */
  unassignPlan(testPlanId: number, userId: number): Observable<TestPlan> {
    if (environment.useMockData) return of(mockAssignedPlan(testPlanId, 0, ''));
    return this.utilityService.deleteRequest<TestPlan>(
      `${this.testPlanEndpoint}/${testPlanId}/Assign/${userId}`,
    );
  }

  /** Manager reassignment (PBI 55096): assign to any tester, recording the acting user. */
  reassignPlan(
    testPlanId: number,
    assigneeUserId: number,
    actingUserId: number,
  ): Observable<TestPlan> {
    if (environment.useMockData) {
      const u = mockUsers.find((m) => m.userId === assigneeUserId);
      return of(
        mockAssignedPlan(
          testPlanId,
          assigneeUserId,
          u ? `${u.firstName} ${u.lastName}` : 'Tester',
        ),
      );
    }
    return this.utilityService.postRequest<TestPlan>(
      `${this.testPlanEndpoint}/${testPlanId}/Assign/${assigneeUserId}?actingUserId=${actingUserId}`,
      {},
    );
  }

  /** Active users for the manager assignment picker. */
  getAssignableUsers(): Observable<TeamMember[]> {
    if (environment.useMockData) return of(mockUsers);
    return this.utilityService.getRequest<TeamMember[]>(`${this.userEndpoint}/All`);
  }
}
